// The composer's rich-text editor brain: owns the contentEditable element, its
// empty/length state, mention insertion (with caret save/restore), and the
// paste/beforeinput length guards.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import type { EntityRef, MessageSegment } from '@interfaces/ICommsApi';
import styles from '../Thread/Composer/Composer.module.css';

const MAX_TEXT_LENGTH = 1000;
const CHAR_COUNT_THRESHOLD = 200; // show the remaining-count chip within this of the cap

// Walks the editor's DOM tree and produces a typed segment list.
// Mention tokens are <span data-mention-kind="..." data-mention-id="..." data-mention-label="...">.
function serializeEditor(root: HTMLElement): MessageSegment[] {
  const out: MessageSegment[] = [];
  const pushText = (text: string) => {
    if (!text) return;
    const last = out[out.length - 1];
    if (last && last.kind === 'text') last.text += text;
    else out.push({ kind: 'text', text });
  };
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushText(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const kind = el.getAttribute('data-mention-kind');
    const id = el.getAttribute('data-mention-id');
    const label = el.getAttribute('data-mention-label');
    if (kind && id && label) {
      out.push({ kind: 'mention', ref: { kind: kind as EntityRef['kind'], id, label } });
      return;
    }
    if (el.tagName === 'BR') {
      pushText('\n');
      return;
    }
    // Fallback: extract text from unknown wrapper.
    pushText(el.textContent ?? '');
  });
  return out;
}

function placeCaretAfter(node: Node) {
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function isEditorEmpty(root: HTMLElement): boolean {
  if (root.childNodes.length === 0) return true;
  return (root.textContent ?? '').length === 0 && root.querySelector('[data-mention-kind]') === null;
}

export interface IMessageEditor {
  isEmpty: boolean;
  textLength: number;
  maxLength: number;
  overLimit: boolean;
  nearLimit: boolean;
  remaining: number;
  mentionOpen: boolean;
  closeMention: () => void;
  focus: () => void;
  insertMention: (ref: EntityRef) => void;
  serialize: () => MessageSegment[];
  clear: () => void;
  onInput: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
  onBeforeInput: (e: { nativeEvent: Event }) => void;
  onPaste: (e: ClipboardEvent<HTMLDivElement>) => void;
}

// The consumer owns the contentEditable ref (and renders the element), 
// the hook just operates on it
export function useMessageEditor(editorRef: RefObject<HTMLDivElement | null>): IMessageEditor {
  // Selection saved at the moment the user pressed `@`. The picker's autoFocus
  // input steals focus, which clears the caret — we restore from here on insert
  // so the chip lands where they typed.
  const savedRangeRef = useRef<Range | null>(null);

  const [mentionOpen, setMentionOpen] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [textLength, setTextLength] = useState(0);

  const refreshEmpty = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    setIsEmpty(isEditorEmpty(el));
    setTextLength(el.textContent?.length ?? 0);
  }, [editorRef]);

  useEffect(() => { refreshEmpty(); }, [refreshEmpty]);

  const serialize = useCallback((): MessageSegment[] => {
    const el = editorRef.current;
    return el ? serializeEditor(el) : [];
  }, [editorRef]);

  const clear = useCallback(() => {
    editorRef.current?.replaceChildren();
    refreshEmpty();
  }, [editorRef, refreshEmpty]);

  const focus = useCallback(() => editorRef.current?.focus(), [editorRef]);
  const closeMention = useCallback(() => setMentionOpen(false), []);

  const insertMention = useCallback((ref: EntityRef) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const chip = document.createElement('span');
    chip.setAttribute('data-mention-kind', ref.kind);
    chip.setAttribute('data-mention-id', ref.id);
    chip.setAttribute('data-mention-label', ref.label);
    chip.setAttribute('contenteditable', 'false');
    chip.className = `${styles.tokenChip} ${styles[`token_${ref.kind}`] ?? ''}`;
    chip.textContent = `@${ref.label}`;

    const saved = savedRangeRef.current;
    if (saved && el.contains(saved.startContainer)) {
      saved.deleteContents();
      saved.insertNode(chip);
    } else {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(chip);
      } else {
        el.appendChild(chip);
      }
    }

    const space = document.createTextNode(' ');
    chip.after(space);
    placeCaretAfter(space);
    savedRangeRef.current = null;
    setMentionOpen(false);
    refreshEmpty();
  }, [editorRef, refreshEmpty]);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === '@') {
      e.preventDefault();
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
      } else {
        savedRangeRef.current = null;
      }
      setMentionOpen(true);
    }
  }, [editorRef]);

  // Block keystrokes once the limit is reached (printable chars; paste handled
  // separately). preventDefault on the native event, matching prior behaviour.
  const onBeforeInput = useCallback((e: { nativeEvent: Event }) => {
    const el = editorRef.current;
    if (!el) return;
    if ((el.textContent?.length ?? 0) >= MAX_TEXT_LENGTH) {
      e.nativeEvent.preventDefault();
    }
  }, [editorRef]);

  // On paste, insert only as much plain text as fits the remaining budget.
  const onPaste = useCallback((e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const el = editorRef.current;
    if (!el) return;
    const pasted = e.clipboardData.getData('text/plain');
    if (!pasted) return;
    const remaining = MAX_TEXT_LENGTH - (el.textContent?.length ?? 0);
    if (remaining <= 0) return;
    const safe = pasted.slice(0, remaining);
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(safe));
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      el.appendChild(document.createTextNode(safe));
    }
    refreshEmpty();
  }, [editorRef, refreshEmpty]);

  const overLimit = textLength > MAX_TEXT_LENGTH;
  const nearLimit = textLength > MAX_TEXT_LENGTH - CHAR_COUNT_THRESHOLD;
  const remaining = MAX_TEXT_LENGTH - textLength;

  return {
    isEmpty,
    textLength,
    maxLength: MAX_TEXT_LENGTH,
    overLimit,
    nearLimit,
    remaining,
    mentionOpen,
    closeMention,
    focus,
    insertMention,
    serialize,
    clear,
    onInput: refreshEmpty,
    onKeyDown,
    onBeforeInput,
    onPaste,
  };
}
